import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Schedule as ScheduleIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import WeekHeader from './schedule/WeekHeader';
import WeekGrid from './schedule/WeekGrid';
import PostDetailsSlideover from './PostDetailsSlideover';
import { useCalendarEvents } from '../hooks/useCalendarEvents';

// Extend dayjs with ISO week plugin
dayjs.extend(isoWeek);

const ScheduleContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
}));

const EmptyWeekMessage = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
}));

/**
 * ScheduleView Component
 * 
 * Displays posts in a weekly schedule format with full navigation capabilities.
 * Uses the optimized calendar API that properly handles scheduled vs immediate posts.
 * 
 * @param {Object} props
 * @param {Array} props.posts - Array of post objects (deprecated, now uses calendar API)
 * @param {Function} props.onPostClick - Callback when a post is clicked
 * @param {Function} props.onFetchPosts - Callback to fetch posts for a specific week (deprecated)
 * @param {boolean} props.loading - Whether posts are currently being fetched (deprecated)
 */
const ScheduleView = ({ posts = [], onPostClick, onFetchPosts, loading: legacyLoading = false }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    // State management
    const [currentWeek, setCurrentWeek] = useState(dayjs());
    const [selectedPost, setSelectedPost] = useState(null);
    const [slideoverOpen, setSlideoverOpen] = useState(false);

    // Use the optimized calendar events hook
    const monthStr = currentWeek.format('YYYY-MM');
    const {
        events: calendarEvents,
        loading: calendarLoading,
        error: calendarError,
        changeMonth
    } = useCalendarEvents(monthStr);

    // Calculate week boundaries
    const weekStart = currentWeek.startOf('isoWeek');
    const weekEnd = currentWeek.endOf('isoWeek');

    // Filter calendar events for current week using display_date
    const weekPosts = useMemo(() => {
        return calendarEvents.filter(event => {
            // Use display_date if available (from backend), otherwise fall back to start
            const eventDate = dayjs(event.display_date || event.start);
            return eventDate.isSameOrAfter(weekStart, 'day') && 
                   eventDate.isSameOrBefore(weekEnd, 'day');
        });
    }, [calendarEvents, weekStart, weekEnd]);

    // Transform calendar events to post format for compatibility
    const transformedPosts = useMemo(() => {
        return weekPosts.map(event => ({
            id: event.post_id,
            post_text: event.post_text,
            img_url: event.img_url,
            img_alt_text: event.img_alt_text || '',
            // Use display_date for proper positioning
            created_at: event.display_date || event.start,
            scheduled_post_date: event.scheduled_date,
            is_scheduled_post: event.is_scheduled,
            platform_details: event.platform_details || [],
            // Add new visual distinction fields
            event_type: event.event_type || 'immediate',
            status: event.status || 'draft',
            display_date: event.display_date || event.start,
            is_posted: event.is_posted || false,
            is_thread: event.is_thread || false,
            thread_order: event.thread_order || '',
            parent_thread: event.parent_thread || '',
            thread_posts: event.thread_posts || [],
        }));
    }, [weekPosts]);

    // Handle week navigation - update calendar month if needed
    const handlePreviousWeek = () => {
        const previousWeek = currentWeek.subtract(1, 'week');
        setCurrentWeek(previousWeek);
        
        // Change calendar month if we crossed month boundary
        const newMonthStr = previousWeek.format('YYYY-MM');
        if (newMonthStr !== monthStr) {
            changeMonth(newMonthStr);
        }
        
        // Legacy callback support
        if (onFetchPosts) {
            onFetchPosts(previousWeek.toDate());
        }
    };

    const handleNextWeek = () => {
        const nextWeek = currentWeek.add(1, 'week');
        setCurrentWeek(nextWeek);
        
        // Change calendar month if we crossed month boundary
        const newMonthStr = nextWeek.format('YYYY-MM');
        if (newMonthStr !== monthStr) {
            changeMonth(newMonthStr);
        }
        
        // Legacy callback support
        if (onFetchPosts) {
            onFetchPosts(nextWeek.toDate());
        }
    };

    // Handle today navigation
    const handleGoToToday = () => {
        const today = dayjs();
        setCurrentWeek(today);
        
        // Update calendar month
        const todayMonthStr = today.format('YYYY-MM');
        if (todayMonthStr !== monthStr) {
            changeMonth(todayMonthStr);
        }
        
        // Legacy callback support
        if (onFetchPosts) {
            onFetchPosts(today.toDate());
        }
    };

    // Handle post click
    const handlePostClick = (post) => {
        setSelectedPost(post);
        setSlideoverOpen(true);
        if (onPostClick) {
            onPostClick(post);
        }
    };

    // Handle slideover close
    const handleSlideoverClose = () => {
        setSlideoverOpen(false);
        setSelectedPost(null);
    };

    // Determine loading state (use calendar loading or legacy loading)
    const isLoading = calendarLoading || legacyLoading;
    
    // Check if there are any posts for the current week
    const hasPostsForWeek = transformedPosts.length > 0;

    // Show error state if calendar API failed
    if (calendarError && !isLoading) {
        return (
            <ScheduleContainer>
                <WeekHeader
                    currentWeek={currentWeek}
                    onPreviousWeek={handlePreviousWeek}
                    onNextWeek={handleNextWeek}
                    onGoToToday={handleGoToToday}
                    showNavigation={true}
                    showTodayButton={true}
                    loading={false}
                />
                <EmptyWeekMessage>
                    <ScheduleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                        Unable to load calendar events
                    </Typography>
                    <Typography variant="body2">
                        {calendarError}
                    </Typography>
                </EmptyWeekMessage>
            </ScheduleContainer>
        );
    }

    return (
        <ScheduleContainer>
            {/* Week Navigation Header */}
            <WeekHeader
                currentWeek={currentWeek}
                onPreviousWeek={handlePreviousWeek}
                onNextWeek={handleNextWeek}
                onGoToToday={handleGoToToday}
                showNavigation={true}
                showTodayButton={true}
                loading={isLoading}
            />

            {/* Schedule Content */}
            {!isLoading && !hasPostsForWeek ? (
                <EmptyWeekMessage>
                    <ScheduleIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
                    <Typography variant="h6" gutterBottom>
                        No posts scheduled for this week
                    </Typography>
                    <Typography variant="body2">
                        Create a new post to see it appear in your schedule
                    </Typography>
                </EmptyWeekMessage>
            ) : (
                <WeekGrid
                    posts={transformedPosts}
                    currentWeek={currentWeek}
                    onPostClick={handlePostClick}
                    loading={isLoading}
                    height="calc(100vh - 200px)"
                    showEmptyDays={false}
                />
            )}

            {/* Post Details Slideover */}
            <PostDetailsSlideover
                open={slideoverOpen}
                onClose={handleSlideoverClose}
                post={selectedPost}
            />
        </ScheduleContainer>
    );
};

export default ScheduleView; 